/**
 * Mock backend responses for DEV_PREVIEW mode so designers can render every
 * screen in a regular browser. Pure functions, no network. Add new branches
 * as you wire up new endpoints.
 */

const DEV_ADDR = '0x000000000000000000000000000000000000dEaD';
const DEV_FUNDER = '0x000000000000000000000000000000000000bEEF';

const FAKE_TYPED_DATA = {
  domain: { name: 'Torian Dev', version: '1', chainId: 137 },
  types: { Mock: [{ name: 'value', type: 'uint256' }] },
  primaryType: 'Mock',
  message: { value: '0' },
};

const FAKE_TYPED_ARGS = {
  from: DEV_ADDR,
  chainId: 137,
  walletAddress: DEV_ADDR,
  factoryAddress: DEV_FUNDER,
  nonce: '0',
  deadline: String(Math.floor(Date.now() / 1000) + 3600),
  calls: [] as Array<{ target: string; value: string; data: string }>,
};

export function getMockResponse(method: string, path: string): unknown {
  // ---- auth ----
  if (path === '/v1/webapp/auth/exchange' || path === '/v1/webapp/auth/refresh') {
    return {
      token: 'dev-preview-token',
      expiresAt: new Date(Date.now() + 60 * 60_000).toISOString(),
      externalUserId: 'dev-preview',
      telegramUserId: '0',
      userExists: true,
      walletAddress: DEV_ADDR,
      depositWalletAddress: DEV_ADDR,
      language: 'pt',
      hasClobCreds: true,
    };
  }
  if (path === '/v1/webapp/auth/clob-creds') return { ok: true };

  // ---- wallet ----
  if (/\/wallet\/balance$/.test(path)) {
    return { address: DEV_ADDR, pUsdBalance: '1234.56', isDeployed: true };
  }
  if (/\/wallet\/deploy$/.test(path)) {
    return {
      address: DEV_ADDR,
      transactionId: 'dev-tx-deploy',
      transactionHash: '0x' + '0'.repeat(64),
      state: 'CONFIRMED',
    };
  }
  if (/\/wallet\/swap-typed-data$/.test(path)) {
    return {
      typedData: FAKE_TYPED_DATA,
      args: FAKE_TYPED_ARGS,
      amountIn: '1000000',
      minOut: '999000',
    };
  }
  if (/\/wallet\/setup-approvals-typed-data$/.test(path)) {
    return { typedData: FAKE_TYPED_DATA, args: FAKE_TYPED_ARGS };
  }
  if (/\/wallet\/bridge-address$/.test(path)) {
    return {
      polymarketWallet: DEV_ADDR,
      evm: DEV_ADDR,
      svm: null,
      btc: null,
      tron: null,
      note: 'DEV PREVIEW — endereços mockados',
    };
  }
  if (/\/wallet\/transfer-to-bridge-typed-data$/.test(path)) {
    return {
      typedData: FAKE_TYPED_DATA,
      args: FAKE_TYPED_ARGS,
      token: 'usdc',
      tokenAddress: DEV_FUNDER,
      amount: '1000000',
      bridgeAddress: DEV_ADDR,
    };
  }
  if (/\/wallet\/withdraw\/status$/.test(path)) {
    return {
      address: DEV_ADDR,
      pUsdBalance: '1234.56',
      isDeployed: true,
      dailyLimitUsd: 1000,
      dailyUsedUsd: '0',
      dailyRemainingUsd: '1000',
    };
  }
  if (/\/wallet\/withdraw-typed-data$/.test(path)) {
    return {
      withdrawalId: 'dev-withdraw-1',
      typedData: FAKE_TYPED_DATA,
      args: FAKE_TYPED_ARGS,
      tokenAddress: DEV_FUNDER,
      amount: '1000000',
      amountUsd: '1.00',
      toAddress: DEV_ADDR,
      dailyLimitUsd: 1000,
      dailyRemainingUsd: '999',
    };
  }
  if (/\/wallet\/withdraw-relay$/.test(path)) {
    return {
      withdrawalId: 'dev-withdraw-1',
      transactionId: 'dev-tx-withdraw',
      transactionHash: '0x' + '0'.repeat(64),
      state: 'CONFIRMED',
    };
  }
  if (/\/wallet\/batch$/.test(path)) {
    return {
      transactionId: 'dev-tx-batch',
      transactionHash: '0x' + '0'.repeat(64),
      state: 'CONFIRMED',
    };
  }

  // ---- orders ----
  if (/^\/v1\/orders(\?|$)/.test(path) && method === 'GET') {
    const now = Date.now();
    return [
      {
        id: 'dev-order-open-1',
        clobOrderId: '0x' + 'a'.repeat(64),
        tokenId: '1234567890',
        marketSlug: 'torian-2026',
        conditionId: '0x' + 'b'.repeat(64),
        side: 'BUY',
        orderType: 'GTC',
        price: '0.45',
        size: '12',
        expiration: null,
        status: 'OPEN',
        errorMessage: null,
        createdAt: new Date(now - 1000 * 60 * 30).toISOString(),
        updatedAt: new Date(now - 1000 * 60 * 30).toISOString(),
        marketQuestion: 'Will Polymarket integrate Torian in 2026?',
        outcome: 'Yes',
        marketUrl: 'https://polymarket.com/event/torian-2026',
      },
      {
        id: 'dev-order-pending-1',
        clobOrderId: null,
        tokenId: '9876543210',
        marketSlug: 'fed-cut-jul-2026',
        conditionId: '0x' + 'c'.repeat(64),
        side: 'SELL',
        orderType: 'GTC',
        price: '0.72',
        size: '5',
        expiration: null,
        status: 'PENDING',
        errorMessage: null,
        createdAt: new Date(now - 1000 * 60 * 2).toISOString(),
        updatedAt: new Date(now - 1000 * 60 * 2).toISOString(),
        marketQuestion: 'Will the Fed cut rates in July 2026?',
        outcome: 'No',
        marketUrl: 'https://polymarket.com/event/fed-cut-jul-2026',
      },
    ];
  }
  if (/^\/v1\/orders\/[^/]+$/.test(path) && method === 'DELETE') {
    return {
      id: path.split('/').pop(),
      userId: 'dev-user',
      clobOrderId: '0x' + 'a'.repeat(64),
      tokenId: '1234567890',
      marketSlug: 'torian-2026',
      conditionId: '0x' + 'b'.repeat(64),
      side: 'BUY',
      orderType: 'GTC',
      price: '0.45',
      size: '12',
      expiration: null,
      builderCode: '0x' + '0'.repeat(64),
      status: 'CANCELLED',
      errorMessage: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }
  if (/\/v1\/orders\/[^/]+\/params$/.test(path)) {
    return {
      summary: {
        orderId: 'dev-order',
        side: 'BUY',
        outcomeLabel: 'Yes',
        marketQuestion: 'Will Polymarket integrate Torian in 2026?',
        marketSlug: 'torian-2026',
        priceCents: 64,
        size: 10,
        totalUsd: 6.4,
        potentialPayoutUsd: 10,
        expiresAt: new Date(Date.now() + 60_000).toISOString(),
      },
      typedData: FAKE_TYPED_DATA,
    };
  }
  if (/\/v1\/orders\/[^/]+\/submit$/.test(path)) {
    return { id: 'dev-order', status: 'SUBMITTED', errorMessage: null };
  }

  // ---- leaderboard ----
  if (/^\/v1\/leaderboard(\?|$)/.test(path) && method === 'GET') {
    return [
      { rank: 1, externalUserId: 'dev-user-1', telegramUsername: 'alice', volumeUsd: 184_320, tradeCount: 92 },
      { rank: 2, externalUserId: 'dev-user-2', telegramUsername: 'bob', volumeUsd: 121_544, tradeCount: 64 },
      { rank: 3, externalUserId: 'dev-user-3', telegramUsername: 'carol', volumeUsd: 88_900, tradeCount: 47 },
      { rank: 4, externalUserId: 'dev-user-4', telegramUsername: null, volumeUsd: 54_210, tradeCount: 31 },
      { rank: 5, externalUserId: 'dev-user-5', telegramUsername: 'eve', volumeUsd: 32_180, tradeCount: 22 },
      { rank: 6, externalUserId: 'dev-user-6', telegramUsername: 'frank', volumeUsd: 18_900, tradeCount: 17 },
      { rank: 7, externalUserId: 'dev-user-7', telegramUsername: 'grace', volumeUsd: 9_540, tradeCount: 11 },
      { rank: 8, externalUserId: 'dev-user-8', telegramUsername: null, volumeUsd: 4_120, tradeCount: 6 },
    ];
  }

  // ---- copy ----
  if (path === '/v1/copy' && method === 'GET') {
    const now = Date.now();
    return [
      {
        followerUserId: 'dev-user',
        leaderAddress: '0x1234567890abcdef1234567890abcdef12345678',
        sizeMultiplier: 1,
        maxUsdPerTrade: 50,
        active: true,
        createdAt: new Date(now - 1000 * 60 * 60 * 24).toISOString(),
      },
      {
        followerUserId: 'dev-user',
        leaderAddress: '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd',
        sizeMultiplier: 2,
        maxUsdPerTrade: 100,
        active: true,
        createdAt: new Date(now - 1000 * 60 * 60 * 4).toISOString(),
      },
    ];
  }
  if (path === '/v1/copy/follow' && method === 'POST') {
    return {
      followerUserId: 'dev-user',
      leaderAddress: '0xnewleaderaddress00000000000000000000000a',
      sizeMultiplier: 1,
      maxUsdPerTrade: 50,
      active: true,
      createdAt: new Date().toISOString(),
    };
  }
  if (/^\/v1\/copy\/[^/]+$/.test(path) && method === 'DELETE') {
    return undefined;
  }

  // ---- fallback ----
  // eslint-disable-next-line no-console
  console.warn(`[torian dev-preview] no mock for ${method} ${path} — returning {}`);
  return {};
}
